// lib/leasing/analystWorkbook.ts
import { z } from 'zod';
import type { Prisma } from '#generated/prisma-postgres/index.js';
import type { TableDoc, TableSheet } from '#types/types';
import { currentSignedLease } from '#lib/leasing/currentSignedLease';

export const analystDocId = (propertyId: string): string => `asset:${propertyId}`;
export const analystStructuralSheetIds = (propertyId: string): string[] =>
  [`${analystDocId(propertyId)}:rent-roll`, `${analystDocId(propertyId)}:lease-dates`];

const keySchema = z.string().regex(/^(0|[1-9]\d{0,4}),(0|[1-9]\d{0,2})$/);
const colorSchema = z.string().max(100);
const styleSchema = z.object({
  bold: z.boolean().optional(), italic: z.boolean().optional(), underline: z.boolean().optional(),
  color: colorSchema.optional(), background: colorSchema.optional(),
  align: z.enum(['left', 'center', 'right']).optional(),
  format: z.string().max(100).optional(), numFmt: z.string().max(200).optional(),
  border: z.object({ top: colorSchema.optional(), right: colorSchema.optional(), bottom: colorSchema.optional(), left: colorSchema.optional() }).strict().optional(),
}).strict();
const sheetSchema = z.object({
  id: z.string().min(1).max(150), name: z.string().trim().min(1).max(100),
  cells: z.record(keySchema, z.string().max(20000)),
  styles: z.record(keySchema, styleSchema).optional(), paper: z.boolean().optional(),
}).strict();

export function validateAnalystWorkbook(input: unknown, saved: TableDoc): TableSheet[] {
  if (JSON.stringify(input)?.length > 2_000_000) throw new Error('Workbook exceeds the 2 MB limit.');
  const parsed = z.object({
    id: z.literal(saved.id), propertyId: z.literal(saved.propertyId!),
    sheets: z.array(sheetSchema).min(2).max(50),
  }).strict().parse(input);
  if (new Set(parsed.sheets.map((s) => s.id)).size !== parsed.sheets.length ||
      new Set(parsed.sheets.map((s) => s.name.toLowerCase())).size !== parsed.sheets.length) {
    throw new Error('Sheet IDs and names must be unique.');
  }
  const required = analystStructuralSheetIds(saved.propertyId!);
  for (const [index, name] of ['Rent Roll', 'Lease Dates'].entries()) {
    if (!parsed.sheets.some((s) => s.id === required[index] && s.name === name)) {
      throw new Error('Rent Roll and Lease Dates cannot be removed or renamed.');
    }
  }
  for (const sheet of parsed.sheets) {
    let rows = 1; let columns = 1;
    for (const key of [...Object.keys(sheet.cells), ...Object.keys(sheet.styles ?? {})]) {
      const [r, c] = key.split(',').map(Number);
      rows = Math.max(rows, r! + 1); columns = Math.max(columns, c! + 1);
    }
    if (Math.max(40, rows + 8) * Math.max(26, columns + 6) > 50_000) throw new Error('Each shared sheet is limited to a 50,000-cell grid.');
  }
  return parsed.sheets;
}

type SourceUnit = Prisma.UnitGetPayload<{ include: { deals: { include: { brand: { select: { name: true } } } } } }>;

export function buildAnalystWorkbook(property: { id: string; name: string }, units: SourceUnit[], at = new Date()): TableDoc {
  const text = (value: unknown): string => value == null ? '' : String(value);
  const date = (value: Date | null): string => value ? value.toISOString().slice(0, 10) : '';
  const rentRows: string[][] = [['Unit', 'Tenant', 'Occupancy', 'Area (m²)', 'Contractual rent', 'Currency', 'Rent basis', 'Payment frequency', 'Lease start', 'Lease expiry', 'Recorded deal ID']];
  const dateRows: string[][] = [['Unit', 'Tenant', 'Break options', 'Break date', 'Lease expiry', 'Indexation type', 'Indexation date', 'Notice date', 'Rent commencement', 'Possession', 'Opening', 'Recorded deal ID']];
  for (const unit of [...units].sort((a, b) => a.unitIdentifier.localeCompare(b.unitIdentifier, 'en', { numeric: true }) || a.id.localeCompare(b.id))) {
    const deal = currentSignedLease(unit, unit.deals, at);
    const tenant = deal?.tenantLegalName || deal?.brand.name || '';
    rentRows.push([unit.unitIdentifier, tenant, unit.status, text(unit.areaSqm), text(deal?.rentAmount), text(deal?.rentCurrency), text(deal?.rentBasis), text(deal?.rentPaymentFrequency), date(deal?.leaseStartDate ?? null), date(deal?.leaseEndDate ?? null), deal?.id ?? '']);
    // There are no recorded break, indexation or notice date columns. Terms remain verbatim.
    dateRows.push([unit.unitIdentifier, tenant, text(deal?.leaseBreakOptions), '', date(deal?.leaseEndDate ?? null), text(deal?.rentIndexationType), '', '', date(deal?.rentCommencementDate ?? null), date(deal?.possessionDate ?? null), date(deal?.openingDate ?? null), deal?.id ?? '']);
  }
  const ids = analystStructuralSheetIds(property.id);
  const sheets = [rentRows, dateRows].map((rows, index): TableSheet => ({
    id: ids[index]!, name: index === 0 ? 'Rent Roll' : 'Lease Dates',
    cells: Object.fromEntries(rows.flatMap((row, r) => row.flatMap((value, c) => value === '' ? [] : [[`${r},${c}`, value]]))),
    styles: Object.fromEntries(rows[0]!.map((_, c) => [`0,${c}`, { bold: true, background: '#1B1B1B', color: '#ECECEC' }])),
  }));
  return { id: analystDocId(property.id), propertyId: property.id, name: property.name, createdAt: at.getTime(), updatedAt: at.getTime(), sheets };
}
